// src/components/order/OrderStatusMonitor.tsx
import React, { useEffect, useState } from 'react';
import { 
  Package, 
  CheckCircle,
  X,
  Truck
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotification } from '../../../context/NotificationContext';
import { useCart } from '../../../context/CartContext';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../types';
import { useAuth } from '../../../context/AuthContext';

interface OrderStatusMonitorProps {
  onStatusChange?: (orderId: number, oldStatus: string, newStatus: string) => void;
}

const OrderStatusMonitor: React.FC<OrderStatusMonitorProps> = ({
  onStatusChange
}) => {
  const { notify } = useNotification();
  const { orders, fetchOrders, updateOrderStatus } = useCart();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showDeliveryPrompt, setShowDeliveryPrompt] = useState(false);
  const [promptOrder, setPromptOrder] = useState<Order | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Get user ID
  useEffect(() => {
    const getUserData = () => {
      try {
        const storedUser = localStorage.getItem('user');
        let userId = null;
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          userId = userObj?.id || userObj?.userId || null;
        }
        setUserId(userId);
      } catch (error) {
        console.error('Error reading user data:', error);
      }
    };

    getUserData();
    
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        getUserData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user,orders]);

  // Check for shipped orders and set prompt order
  useEffect(() => {
    if (!userId || !orders.length) return;

    // console.log('All orders:', orders);
    // console.log('User ID:', userId);

    // Find shipped orders for current user
    const shippedOrders = orders.filter(order => {
      const isShipped = order.status?.toLowerCase() === 'shipped';
      
      // Check if order belongs to current user
      const isUserOrder = 
        (order.customer.toString() === userId) ;
      
      // console.log(`Order ${order.id}: isShipped=${isShipped}, isUserOrder=${isUserOrder}`);
      
      return isShipped && isUserOrder;
    });
    
    // console.log('Shipped orders:', shippedOrders);

    if (shippedOrders.length > 0 && !promptOrder) {
      // Get the most recent shipped order
      const latestOrder = shippedOrders.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
      
      // console.log('Latest shipped order:', latestOrder);
      
      setPromptOrder(latestOrder);
      
      // Show prompt after 1 second (التلقائي)
      const timer = setTimeout(() => {
        setShowDeliveryPrompt(true);
        notify('info', t('orderMonitor.deliveryArrivedPrompt', 'Your order has arrived!'));
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [orders, userId, t, notify, promptOrder, user]);

  // Function to open modal manually
  const openDeliveryPrompt = () => {
    if (shippedOrdersCount > 0 && !promptOrder) {
      // إعادة استخدام المنطق لإيجاد الطلب
      const shippedOrders = orders.filter(order => {
        const isShipped = order.status?.toLowerCase() === 'shipped';
        const isUserOrder = userId !== null && `${order.customer}` ===`${userId}`;
        return isShipped && isUserOrder;
      });
      
      if (shippedOrders.length > 0) {
        const latestOrder = shippedOrders.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0];
        setPromptOrder(latestOrder);
      }
    }
    setShowDeliveryPrompt(true);
  };

  // Confirm delivery
  const confirmOrderDelivery = async (orderId: number) => {
    setIsConfirming(true);
    try {
      // Update order status to delivered
      await updateOrderStatus(orderId, 'delivered');
      
      // Refresh orders
      await fetchOrders();
      
      // Close prompt
      setShowDeliveryPrompt(false);
      setPromptOrder(null);

      // Show success notification
      notify('success', t('orderMonitor.orderConfirmed', 'Order confirmed successfully!'));
      
      // Trigger callback
      if (onStatusChange) {
        onStatusChange(orderId, 'shipped', 'delivered');
      }
      
    } catch (error) {
      console.error('Error confirming order:', error);
      notify('error', t('orderMonitor.confirmationError', 'Failed to confirm order'));
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle confirm click
  const handleConfirmDelivery = () => {
    if (promptOrder) {
      confirmOrderDelivery(promptOrder.id);
    }
  };

  // Get shipped orders count for badge
  const shippedOrdersCount = orders.filter(order => {
    const isShipped = order.status?.toLowerCase() === 'shipped';
    const isUserOrder = userId !== null && `${order.customer}` === `${userId}`;
    return isShipped && isUserOrder;
  }).length;

  // If no user or no shipped orders, show nothing
  if (!userId) {
    return null;
  }

  return (
    <>
      {/* Small Notification Badge - يظهر دائماً إذا هناك طلبات shipped */}
      {shippedOrdersCount > 0   && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          className="fixed bottom-6 right-6 z-40"
        >
          <button
            onClick={openDeliveryPrompt} // التصحيح هنا!
            className="flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-3 rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-200 hover:scale-105"
          >
            <div className="relative">
              <Truck className="h-6 w-6" />
              <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full text-xs flex items-center justify-center animate-pulse">
                {shippedOrdersCount}
              </span>
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm">
                {t('orderMonitor.ordersArrived', 'Order Arrived!')}
              </p>
              <p className="text-xs opacity-90">
                {t('orderMonitor.tapToConfirm', 'Tap to confirm')}
              </p>
            </div>
            <div className="animate-bounce">
              <CheckCircle className="h-5 w-5" />
            </div>
          </button>
        </motion.div>
      )}

      {/* Delivery Prompt Modal */}
      <AnimatePresence>
        {showDeliveryPrompt && promptOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Truck className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">
                        {t('orderMonitor.deliveryPrompt.title', 'Order Arrived!')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('orderMonitor.orderNumber', 'Order')} #{promptOrder.id}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowDeliveryPrompt(false);
                      setPromptOrder(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package className="h-12 w-12 text-blue-500" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    {t('orderMonitor.deliveryPrompt.question', 'Has your order arrived?')}
                  </h4>
                  <p className="text-gray-600">
                    {t('orderMonitor.deliveryPrompt.description', 'Please confirm delivery to complete your order')}
                  </p>
                </div>

                {/* Order Summary */}
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Order ID</p>
                      <p className="font-semibold">#{promptOrder.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Items</p>
                      <p className="font-semibold">{promptOrder.items?.length || 0}</p>
                    </div>
                    {promptOrder.total && (
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500">Total Amount</p>
                        <p className="font-bold text-lg text-blue-600">
                          ${typeof promptOrder.total === 'number' ? promptOrder.total : promptOrder.total}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Button - Only YES button */}
                <button
                  onClick={handleConfirmDelivery}
                  disabled={isConfirming}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {isConfirming ? (
                    <>
                      <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('orderMonitor.deliveryPrompt.confirming', 'Confirming...')}</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-6 w-6" />
                      <span>{t('orderMonitor.deliveryPrompt.yesArrived', 'Yes, it arrived! Confirm Now')}</span>
                    </>
                  )}
                </button>

                {/* Close button */}
                <button
                  onClick={() => {
                    setShowDeliveryPrompt(false);
                    setPromptOrder(null);
                  }}
                  className="w-full mt-3 text-gray-500 hover:text-gray-700 py-2 text-sm"
                >
                  {t('orderMonitor.deliveryPrompt.notNow', 'Not now')}
                </button>
              </div>

              {/* Footer */}
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  {t('orderMonitor.deliveryPrompt.helpText', 'Confirming helps us provide better service')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default OrderStatusMonitor;